import { useTranslations } from "next-intl";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import { useParams } from "next/navigation";
import { FC, useEffect, useRef, useState } from "react";
import SwiperCore from "swiper";
// import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import Icon from "@/components/ui/Icon/Icon";
import useMediaQuery from "@/hooks/useMediaQuery";
import { GuidedToolProps, SearchParams } from "@/types";

import { GuidedToolStateStep, useGuidedToolContext } from "./GuidedToolProvider";
import ModalToolSlide from "./ModalToolSlide";

const Modal = dynamic(() => import("../ui/Modal/Modal"), { ssr: false });
const ModalContent = dynamic(() => import("../ui/Modal/ModalContent"), { ssr: false });

const GuidedToolModal: FC<GuidedToolProps> = ({ guidedTool, modalOpen, closeModal }) => {
  const t = useTranslations();
  const { state, setState, filters, currentAnswer, setCurrentAnswer, currentFilter, setCurrentFilter, setActiveSlideId } = useGuidedToolContext();
  const urlParams = useParams<{ locale: string }>();
  const swiperRef = useRef<SwiperCore>();
  const [swiperIndex, setSwiperIndex] = useState(0);
  const router = useRouter();

  useEffect(() => {
    // cleanup temp states after slide change
    setCurrentAnswer(undefined);
    setCurrentFilter(undefined);
    setActiveSlideId(guidedTool.slides[swiperIndex].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guidedTool.slides, swiperIndex]);

  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const nextStep = (skip?: boolean) => {
    let newState = [...state];
    if (!skip) {
      const newItem: GuidedToolStateStep = {
        id: guidedTool.slides[swiperIndex].id,
        type: guidedTool.slides[swiperIndex].type,
        question: guidedTool.slides[swiperIndex].question,
        ...(currentAnswer && { answer: currentAnswer }),
        ...(currentFilter && { filter: currentFilter }),
      };
      // if the state already contains the slide id replace it
      const slideIndex = state.findIndex((slide) => slide.id == guidedTool.slides[swiperIndex].id);
      if (slideIndex == -1) {
        newState = [...newState, newItem];
      } else {
        newState[slideIndex] = newItem;
      }
      setState(newState);
    }
    if (swiperRef.current?.isEnd) {
      localStorage.setItem("guidedTool", JSON.stringify(newState));
      const filterObjects = newState.filter((item) => item.type === "filter");
      const queryParams = filterObjects.flatMap((item) =>
        Object.entries(item.filter || {}).map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`),
      );
      router?.push(`/${urlParams?.locale}/search/?${queryParams?.join("&")}`);
    } else {
      swiperRef.current?.slideNext();
    }
  };

  if (!guidedTool || Object.keys(filters).length == 0 || !modalOpen) {
    return <></>;
  }

  return (
    <Modal open={modalOpen} onClose={() => closeModal()}>
      <ModalContent className="h-full !rounded-none !p-0 w-full">
        <div className="px-m py-l flex justify-between item-center sticky bg-white top-0 z-20 gt-modal-title">
          <h2 className="font-primary text-m font-medium uppercase">{guidedTool.title}</h2>
          <Button
            className="modal__close"
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.TERTIARY}
            theme={ButtonTheme.BLACK}
            onClick={() => closeModal()}
          >
            <span className="pointer-events-none">
              <Icon name="close" size="24" title="close" className="stroke-black" />
              <span className="sr-only">{t("button.label.closeModal")}</span>
            </span>
          </Button>
        </div>
        <div
          className="h-full   md:h-[calc(100dvh-72px)] bg-cover bg-center relative before:content-[''] before:absolute before:h-full before:w-full before:bg-[rgba(0,0,0,0.4)] before:top-0 before:left-0 gt-bg-image"
          style={{ backgroundImage: `url(${guidedTool.background?.url}` }}
        >
          <div className="overflow-auto max-h-[calc(100%-64px)] md:max-h-full md:h-full pb-xl md:pb-0 no-scrollbar">
            <div className="flex justify-center flex-row pb-s md:pb-l pt-l gt-button-wrapper">
              <div className="w-22\/24 md:w-20\/24 z-10">
                <div className={`flex ${swiperIndex >= 1 ? "justify-between" : "justify-end"}`}>
                  {swiperIndex >= 1 && (
                    <Button
                      size={ButtonSize.SMALL}
                      variant={ButtonVariant.TERTIARY}
                      theme={ButtonTheme.WHITE}
                      customClass="pl-0"
                      onClick={() => swiperRef.current?.slidePrev()}
                    >
                      <span className="pointer-events-none inline-flex items-center gap-xxs">
                        <Icon className="stroke-inherit" name="chevron-left" size="24" title="chevron-left" />
                        <span className="underline normal-case font-secondary font-medium text-m leading-6 gt-prev-slide">
                          {t("button.label.previousquestion")}
                        </span>
                      </span>
                    </Button>
                  )}
                  <div className="flex">
                    <Button size={ButtonSize.SMALL} variant={ButtonVariant.TERTIARY} theme={ButtonTheme.WHITE} customClass="gt-top-button">
                      <span className="pointer-events-none inline-flex items-center gap-xxs">
                        <span className="underline normal-case hidden md:inline-block font-secondary font-medium text-m leading-6">
                          {t("button.label.callUs")}
                        </span>
                        <Icon name="phone" size="24" title="phone" />
                      </span>
                    </Button>
                    <Button size={ButtonSize.SMALL} variant={ButtonVariant.TERTIARY} theme={ButtonTheme.WHITE} customClass="pr-0 gt-top-button">
                      <span className="pointer-events-none inline-flex items-center gap-xxs">
                        <span className="underline normal-case  hidden md:inline-block  font-secondary font-medium text-m leading-6">
                          {t("button.label.messageUs")}
                        </span>
                        <Icon name="chat" size="24" title="message" />
                      </span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <div className="pb-xxl min-h-[calc(100dvh-212px)] md:h-[calc(100dvh-154px)]">
              <Swiper
                onSwiper={(swiper) => (swiperRef.current = swiper)}
                onActiveIndexChange={(swiper) => {
                  setSwiperIndex(swiper.activeIndex);
                }}
                className="guided-tool-carousel h-full"
                slidesPerView={"auto"}
                spaceBetween={24}
                allowTouchMove={false}
                centeredSlides={true}
                autoHeight={isDesktop ? false : true}
                watchSlidesProgress={true}
              >
                {guidedTool.slides.map((slide, i) => (
                  <SwiperSlide
                    className="!w-22\/24 md:!w-18\/24 lg:!w-20\/24 rounded-2xl py-l px-m md:p-[40px] box-material-primary backdrop-blur-xl text-white min-h-[calc(100svh-240px)] md:min-h-[auto] gt-slide"
                    key={`guided-tool-carousel-${i}`}
                  >
                    <ModalToolSlide slide={slide} />

                    <div className="text-right mt-m md:mt-xl  md:absolute md:bottom-[40px] md:right-[40px] gt-slide__button">
                      <Button
                        size={ButtonSize.MEDIUM}
                        variant={ButtonVariant.SECONDARY}
                        theme={ButtonTheme.WHITE}
                        onClick={() => nextStep(true)}
                        customClass="w-full md:w-auto md:mr-s "
                      >
                        {t("button.label.nopreferences")}
                      </Button>
                      {isDesktop && (
                        <Button
                          size={ButtonSize.MEDIUM}
                          variant={ButtonVariant.PRIMARY}
                          theme={ButtonTheme.WHITE}
                          customClass="border-white"
                          onClick={() => nextStep()}
                        >
                          {t("button.label.confirmselection")}
                        </Button>
                      )}
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
              {!isDesktop && (
                <div className="bg-white fixed w-full bottom-0 p-[10px] z-20">
                  <Button
                    size={ButtonSize.MEDIUM}
                    variant={ButtonVariant.PRIMARY}
                    theme={ButtonTheme.BLACK}
                    onClick={() => nextStep()}
                    customClass="w-full"
                  >
                    {t("button.label.confirmselection")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default GuidedToolModal;
